import { DocumentModel } from './templates/document.model'

export interface EducationSummary {
  level: string | null
  hasOLevel: boolean | null
  oLevelTimes: number | null
  oLevelTypes: string[] | null
}

export interface EligibilityResult {
  passed: boolean | null
  reasons: string[] | null
}

export interface ApplicationInstallment {
  id: string | null
  application_id: string | null
  installment_number?: number | null
  percentage?: number | null
  amount?: number | null
  amount_paid?: number | null
  due_at?: string | null
  is_paid?: boolean | null
  paid_at?: string | null
  is_active?: boolean | null
  created_at?: string | null
  updated_at?: string | null
}

export interface ApplicationPayment {
  id: string | null
  application_id: string | null
  application_document_id?: string | null
  amount: number | null
  currency: string | null
  reference: string | null
  provider: string | null
  status: string | null
  receipt_storage_key?: string | null
  receipt_url?: string | null
  verified_by: string | null
  verification_note: string | null
  verified_at: string | null
  created_at: string | null
  updated_at: string | null
}

export class ApplicationPaymentModel implements ApplicationPayment {
  id: string | null = null
  application_id: string | null = null
  application_document_id: string | null = null
  amount: number | null = null
  currency: string | null = null
  reference: string | null = null
  provider: string | null = null
  status: string | null = null
  receipt_storage_key: string | null = null
  receipt_url: string | null = null
  verified_by: string | null = null
  verification_note: string | null = null
  verified_at: string | null = null
  created_at: string | null = null
  updated_at: string | null = null

  constructor(data?: Partial<ApplicationPayment>) {
    Object.assign(this, data)
  }

  static fromJSON(json: any): ApplicationPaymentModel {
    if (!json) return new ApplicationPaymentModel()
    const parsedAmount =
      typeof json.amount === 'number'
        ? json.amount
        : typeof json.amount === 'string'
          ? Number.parseFloat(json.amount)
          : null

    return new ApplicationPaymentModel({
      id: json.id ?? null,
      application_id: json.application_id ?? null,
      application_document_id: json.application_document_id ?? null,
      amount: Number.isNaN(parsedAmount) ? null : parsedAmount,
      currency: json.currency ?? null,
      reference: json.reference ?? null,
      provider: json.provider ?? null,
      status: json.status ?? null,
      receipt_storage_key: json.receipt_storage_key ?? null,
      receipt_url: json.receipt_url ?? null,
      verified_by: json.verified_by ?? null,
      verification_note: json.verification_note ?? null,
      verified_at: json.verified_at ?? null,
      created_at: json.created_at ?? null,
      updated_at: json.updated_at ?? null,
    })
  }

  toJSON(): Record<string, any> {
    return {
      id: this.id,
      application_id: this.application_id,
      application_document_id: this.application_document_id,
      amount: this.amount,
      currency: this.currency,
      reference: this.reference,
      provider: this.provider,
      status: this.status,
      receipt_storage_key: this.receipt_storage_key,
      receipt_url: this.receipt_url,
      verified_by: this.verified_by,
      verification_note: this.verification_note,
      verified_at: this.verified_at,
      created_at: this.created_at,
      updated_at: this.updated_at,
    }
  }
}

export class ApplicationInstallmentModel implements ApplicationInstallment {
  id: string | null = null
  application_id: string | null = null
  installment_number?: number | null
  percentage?: number | null
  amount?: number | null
  amount_paid?: number | null
  due_at?: string | null
  is_paid?: boolean | null
  paid_at?: string | null
  is_active?: boolean | null
  created_at?: string | null
  updated_at?: string | null

  constructor(data?: Partial<ApplicationInstallment>) {
    Object.assign(this, data)
  }

  static fromJSON(json: any): ApplicationInstallmentModel {
    if (!json) return new ApplicationInstallmentModel()

    return new ApplicationInstallmentModel({
      id: json.id ?? null,
      application_id: json.application_id ?? null,
      installment_number: json.installment_number ?? null,
      percentage: json.percentage ?? null,
      amount: parseAmount(json.amount),
      amount_paid: parseAmount(json.amount_paid),
      due_at: json.due_at ?? null,
      is_paid: json.is_paid ?? null,
      paid_at: json.paid_at ?? null,
      is_active: json.is_active ?? null,
      created_at: json.created_at ?? null,
      updated_at: json.updated_at ?? null,
    })
  }

  toJSON(): Record<string, unknown> {
    return {
      id: this.id,
      application_id: this.application_id,
      installment_number: this.installment_number ?? null,
      percentage: this.percentage ?? null,
      amount: this.amount ?? null,
      amount_paid: this.amount_paid ?? null,
      due_at: this.due_at ?? null,
      is_paid: this.is_paid ?? null,
      paid_at: this.paid_at ?? null,
      is_active: this.is_active ?? null,
      created_at: this.created_at ?? null,
      updated_at: this.updated_at ?? null,
    }
  }

  get completionRatio(): number {
    const amount = this.amount ?? 0
    const paid = this.amount_paid ?? 0
    if (amount > 0) {
      return Math.min(1, Math.max(0, paid / amount))
    }
    if (this.is_paid) return 1
    return 0
  }

  get percentageContribution(): number {
    const base = this.percentage ?? 0
    return Math.max(0, Math.min(100, base * this.completionRatio))
  }
}

export enum ApplicationStatus {
  DRAFT = 'draft',
  SUBMITTED = 'submitted',
  UNDER_REVIEW = 'under_review',
  PARTIAL_ADMIT = 'partial_admit',
  ADMITTED = 'admitted',
  REJECTED = 'rejected',
  WITHDRAWN = 'withdrawn',
}

export interface Application {
  id: string | null
  app_id: string | null
  programme_id: string | null
  programme_cohort_id: string | null
  first_name: string | null
  last_name: string | null
  email: string | null
  phone: string | null
  nationality: string | null
  dob: string | null
  address: string | null
  jamb_score: number | null
  education_summary: EducationSummary[] | null
  status: ApplicationStatus | null
  payment_status: string | null
  amount_due?: number | null
  amount_currency?: string | null
  amount_paid?: number | null
  last_payment_reminder_sent_at?: string | null
  payment_plan?: string | null
  payment_due_date?: string | null
  eligibility_result: EligibilityResult | null
  submitted_at: string | null
  created_at: string | null
  updated_at: string | null
  documents: DocumentModel[] | null
  payments: ApplicationPaymentModel[] | null
  installments?: ApplicationInstallmentModel[] | null
}

export class ApplicationModel implements Application {
  id = null
  app_id = null
  programme_id = null
  programme_cohort_id = null
  first_name = null
  last_name = null
  email = null
  phone = null
  nationality = null
  dob = null
  address = null
  jamb_score = null
  education_summary = null
  status = null
  payment_status = null
  amount_due: number | null = null
  amount_currency: string | null = null
  amount_paid: number | null = null
  last_payment_reminder_sent_at: string | null = null
  payment_plan: string | null = null
  payment_due_date: string | null = null
  eligibility_result = null
  submitted_at = null
  created_at = null
  updated_at = null
  documents: DocumentModel[] = []
  payments: ApplicationPaymentModel[] = []
  installments: ApplicationInstallmentModel[] = []

  constructor(data?: Partial<Application>) {
    Object.assign(this, data)
  }

  static fromJSON(json: any): ApplicationModel {
    if (!json) return new ApplicationModel()

    let documents: DocumentModel[] | null = null

    if (Array.isArray(json.documents) && json.documents.length > 0) {
      const parsedDocs: DocumentModel[] = json.documents.map((d: any) => DocumentModel.fromJSON(d))
      parsedDocs.sort((a, b) => {
        const dateA = new Date(a.created_at ?? '').getTime()
        const dateB = new Date(b.created_at ?? '').getTime()
        return dateB - dateA
      })
      const uniqueDocs: Record<string, DocumentModel> = {}
      for (const doc of parsedDocs) {
        const key = doc.application_payment_id ? `${doc.type}-${doc.application_payment_id}` : doc.type ?? ''
        if (key && !uniqueDocs[key]) {
          uniqueDocs[key] = doc
        }
      }
      documents = Object.values(uniqueDocs)
    }

    const payments = Array.isArray(json.payments)
      ? json.payments.map((payment: any) => ApplicationPaymentModel.fromJSON(payment))
      : null
    const installments = Array.isArray(json.installments)
      ? json.installments.map((item: any) => ApplicationInstallmentModel.fromJSON(item))
      : null

    return new ApplicationModel({
      id: json.id ?? null,
      app_id: json.app_id ?? null,
      programme_id: json.programme_id ?? json.program_id ?? null,
      programme_cohort_id: json.programme_cohort_id ?? json.programmeCohortId ?? null,
      first_name: json.first_name ?? null,
      last_name: json.last_name ?? null,
      email: json.email ?? null,
      phone: json.phone ?? null,
      nationality: json.nationality ?? null,
      dob: json.dob ?? null,
      address: json.address ?? null,
      jamb_score: json.jamb_score ?? null,
      education_summary: json.education_summary
        ? json.education_summary.map((e: any) => ({
            level: e.level ?? null,
            hasOLevel: e.hasOLevel ?? null,
            oLevelTimes: e.oLevelTimes ?? null,
            oLevelTypes: e.oLevelTypes ?? null,
          }))
        : null,
      status: json.status ?? null,
      payment_status: json.payment_status ?? null,
      amount_due: parseAmount(json.amount_due),
      amount_currency: json.amount_currency ?? null,
      amount_paid: parseAmount(json.amount_paid),
      last_payment_reminder_sent_at: json.last_payment_reminder_sent_at ?? null,
      payment_plan: json.payment_plan ?? null,
      payment_due_date: json.payment_due_date ?? null,
      eligibility_result: json.eligibility_result
        ? {
            passed: json.eligibility_result.passed ?? null,
            reasons: json.eligibility_result.reasons ?? null,
          }
        : null,
      submitted_at: json.submitted_at ?? null,
      created_at: json.created_at ?? null,
      updated_at: json.updated_at ?? null,
      documents,
      payments,
      installments,
    })
  }

  toJSON(): Record<string, any> {
    return {
      id: this.id,
      app_id: this.app_id,
      programme_id: this.programme_id,
      programme_cohort_id: this.programme_cohort_id,
      first_name: this.first_name,
      last_name: this.last_name,
      email: this.email,
      phone: this.phone,
      nationality: this.nationality,
      dob: this.dob,
      address: this.address,
      jamb_score: this.jamb_score,
      education_summary: this.education_summary,
      status: this.status,
      payment_status: this.payment_status,
      amount_due: this.amount_due,
      amount_currency: this.amount_currency,
      amount_paid: this.amount_paid,
      last_payment_reminder_sent_at: this.last_payment_reminder_sent_at,
      payment_plan: this.payment_plan,
      payment_due_date: this.payment_due_date,
      eligibility_result: this.eligibility_result,
      submitted_at: this.submitted_at,
      created_at: this.created_at,
      updated_at: this.updated_at,
      documents: this.documents ? this.documents.map((doc) => doc.toJSON()) : null,
      payments: this.payments ? this.payments.map((payment) => payment.toJSON()) : null,
      installments: this.installments ? this.installments.map((installment) => installment.toJSON()) : null,
    }
  }

  get totalInstallments(): number {
    return this.installments?.length ?? 0
  }

  get paidInstallments(): number {
    if (!this.installments) return 0
    return this.installments.filter((installment) => installment.completionRatio >= 1 || installment.is_paid).length
  }

  get totalDueAmount(): number | undefined {
    if (Number.isFinite(this.amount_due ?? null)) return this.amount_due ?? undefined
    if (this.installments?.length) {
      const sum = this.installments.reduce((acc, installment) => acc + (installment.amount ?? 0), 0)
      return sum > 0 ? sum : undefined
    }
    return undefined
  }

  get totalPaidAmount(): number | undefined {
    if (Number.isFinite(this.amount_paid ?? null)) return this.amount_paid ?? undefined
    if (this.installments?.length) {
      const sum = this.installments.reduce((acc, installment) => acc + (installment.amount_paid ?? 0), 0)
      return sum > 0 ? sum : undefined
    }
    if (this.payments?.length) {
      const sum = this.payments.reduce((acc, payment) => acc + (payment.amount ?? 0), 0)
      return sum > 0 ? sum : undefined
    }
    return undefined
  }

  get paymentPercentComplete(): number {
    if (this.installments?.length) {
      const percent = this.installments.reduce((acc, installment) => acc + installment.percentageContribution, 0)
      return Math.max(0, Math.min(100, Math.round(percent)))
    }
    const total = this.totalDueAmount
    const paid = this.totalPaidAmount
    if (total && paid !== undefined) {
      return Math.max(0, Math.min(100, Math.round((paid / total) * 100)))
    }
    return 0
  }

  get nextInstallmentDueDate(): string | undefined {
    if (!this.installments?.length) return undefined
    const upcoming = this.installments
      .filter((inst) => !inst.is_paid)
      .sort((a, b) => (a.due_at ?? '').localeCompare(b.due_at ?? ''))
    return upcoming[0]?.due_at ?? undefined
  }
}

function parseAmount(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null
  const numeric = typeof value === 'string' ? Number.parseFloat(value) : (value as number)
  return Number.isFinite(numeric) ? numeric : null
}
